import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, ChevronDown, LogOut, User, Menu, Package } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAdminOrdersStore } from '../../state/ordersStore'
import { formatVND } from '@/lib/formatters'
import { useAdminAuth } from '../../state/authStore'
import { useAdminUiStore } from '../../state/adminUiStore'

export function AdminTopbar() {
  const username = useAdminAuth((s) => s.username) ?? 'admin'
  const logout = useAdminAuth((s) => s.logout)
  const openMobileNav = useAdminUiStore((s) => s.openMobileNav)
  const navigate = useNavigate()

  const [open, setOpen] = useState(false)
  const [bellOpen, setBellOpen] = useState(false)
  const bellRef = useRef<HTMLDivElement>(null)
  const orders = useAdminOrdersStore((s) => s.orders)
  const pendingOrders = orders.filter((o) => o.status === 'pending').slice(0, 10)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!bellRef.current?.contains(e.target as Node)) setBellOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [])
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/admin/dang-nhap', { replace: true })
  }

  return (
    <header className="h-16 bg-surface border-b border-border px-4 lg:px-6 flex items-center justify-between shrink-0 sticky top-0 z-30">
      <div className="flex items-center gap-3">
        <button
          onClick={openMobileNav}
          className="lg:hidden w-10 h-10 rounded-md hover:bg-slate-100 text-text flex items-center justify-center"
          aria-label="Mở menu"
        >
          <Menu className="w-5 h-5" />
        </button>
        <h1 className="font-semibold text-text hidden sm:block">Trang quản trị</h1>
        <span className="text-xs bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full font-medium hidden sm:inline">v1.0</span>
      </div>

      <div className="flex items-center gap-2 lg:gap-3">
        <div className="relative" ref={bellRef}>
          <button
            onClick={() => setBellOpen(!bellOpen)}
            className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center text-text relative"
            aria-label="Thông báo"
          >
            <Bell className="w-5 h-5" />
            {pendingOrders.length > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold flex items-center justify-center">
                {pendingOrders.length > 9 ? '9+' : pendingOrders.length}
              </span>
            )}
          </button>
          {bellOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 bg-surface border border-border rounded-lg shadow-hover z-40 overflow-hidden">
              <div className="px-4 h-12 border-b border-border flex items-center justify-between">
                <span className="font-semibold text-text text-sm">Thông báo</span>
                <span className="text-xs text-muted">{pendingOrders.length} đơn chờ xử lý</span>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {pendingOrders.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted">Không có đơn mới</div>
                ) : (
                  pendingOrders.map((o) => (
                    <Link
                      key={o.id}
                      to={`/admin/don-hang/${o.id}`}
                      onClick={() => setBellOpen(false)}
                      className="flex gap-3 px-4 py-3 border-b border-border last:border-b-0 hover:bg-slate-50 transition"
                    >
                      <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-text truncate">Đơn mới: {o.code}</div>
                        <div className="text-xs text-muted truncate">{o.address.fullName} · {formatVND(o.grandTotal)}</div>
                      </div>
                    </Link>
                  ))
                )}
              </div>
              <Link
                to="/admin/don-hang"
                onClick={() => setBellOpen(false)}
                className="block h-11 flex items-center justify-center text-sm text-brand-600 hover:bg-slate-50 border-t border-border font-medium"
              >
                Xem tất cả đơn hàng
              </Link>
            </div>
          )}
        </div>

        <div className="relative" ref={ref}>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2.5 h-10 pl-1.5 pr-3 rounded-full hover:bg-slate-100 transition"
          >
            <div className="w-8 h-8 rounded-full bg-brand-600 text-white flex items-center justify-center font-semibold text-sm uppercase">
              {username[0]}
            </div>
            <div className="text-left leading-tight hidden sm:block">
              <div className="text-sm font-semibold text-text">{username}</div>
              <div className="text-[11px] text-muted">Quản trị viên</div>
            </div>
            <ChevronDown className="w-4 h-4 text-muted" />
          </button>

          {open && (
            <div className="absolute right-0 top-full mt-2 w-56 bg-surface border border-border rounded-lg shadow-hover py-2 z-40">
              <div className="px-4 py-2 border-b border-border">
                <div className="text-sm font-semibold text-text">{username}</div>
                <div className="text-xs text-muted">Quản trị viên</div>
              </div>
              <button className="w-full px-4 h-10 flex items-center gap-2.5 text-sm text-text hover:bg-slate-50 transition">
                <User className="w-4 h-4" /> Tài khoản
              </button>
              <button
                onClick={handleLogout}
                className="w-full px-4 h-10 flex items-center gap-2.5 text-sm text-danger hover:bg-danger/5 transition"
              >
                <LogOut className="w-4 h-4" /> Đăng xuất
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
